import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Stack,
  Typography,
} from '@mui/material';
import { useFeedback } from '../common/FeedbackProvider';
import { usePushNotifications } from '../../hooks/usePushNotifications';
import {
  getIosPushGuidance,
  getWebPushUnsupportedReason,
  isIosDevice,
  isIosNonSafariBrowser,
  isWebPushSupported,
} from '../../push/webPush';

const LABELS = {
  unsupported: 'Not available in this browser',
  denied: 'Blocked in browser settings',
  default: 'Off — tap Turn on',
  granted: 'Allowed — finishing setup…',
  subscribed: 'On',
  'missing-vapid': 'Not available on this server',
};

export function PushNotificationsCard() {
  const { status, busy, error, enable } = usePushNotifications();
  const { showSuccess } = useFeedback();
  const iosGuidance = getIosPushGuidance();
  const blockIosChrome = isIosDevice() && isIosNonSafariBrowser();

  if (!isWebPushSupported() || blockIosChrome) {
    const reason =
      getWebPushUnsupportedReason() ||
      'This browser does not support Web Push.';
    return (
      <Card>
        <CardContent sx={{ p: { xs: 1.75, sm: 2.5, md: 3 } }}>
          <Stack spacing={1.5}>
            <Typography variant="h6">Phone notifications</Typography>
            <Typography color="text.secondary" sx={{ lineHeight: 1.6 }}>
              {reason}
            </Typography>
            {blockIosChrome ? (
              <Alert severity="warning">
                Install from Safari on this iPhone for OS notifications. The same
                flow works in Chrome on Android.
              </Alert>
            ) : null}
          </Stack>
        </CardContent>
      </Card>
    );
  }

  const canEnable = status === 'default' || status === 'granted';

  return (
    <Card>
      <CardContent sx={{ p: { xs: 1.75, sm: 2.5, md: 3 } }}>
        <Stack spacing={2} alignItems="stretch">
          <Box>
            <Typography variant="h6" sx={{ mb: 0.75 }}>
              Phone notifications
            </Typography>
            <Typography color="text.secondary" sx={{ lineHeight: 1.6 }}>
              Turn these on so alerts reach your phone when a story or message
              goes out.
            </Typography>
          </Box>

          {iosGuidance ? (
            <Alert severity="info" sx={{ alignItems: 'flex-start' }}>
              {iosGuidance}
            </Alert>
          ) : null}

          <Chip
            color={status === 'denied' ? 'error' : 'primary'}
            variant="outlined"
            label={LABELS[status] || status}
            sx={{
              alignSelf: 'flex-start',
              height: 'auto',
              py: 1,
              px: 0.5,
              '& .MuiChip-label': {
                whiteSpace: 'normal',
                lineHeight: 1.4,
                display: 'block',
                py: 0.25,
              },
            }}
          />

          {status === 'denied' && (
            <Alert severity="warning">
              Notifications are blocked for this site. Open your browser site
              settings, allow notifications for this origin, then return here and
              refresh the page.
            </Alert>
          )}

          {error ? <Alert severity="error">{error}</Alert> : null}

          {canEnable && (
            <Button
              variant="contained"
              color="secondary"
              disabled={busy}
              onClick={() =>
                void enable()
                  .then((next) => {
                    if (next === 'subscribed' || next === 'granted') {
                      showSuccess('Notifications on');
                    }
                  })
                  .catch(() => {})
              }
              sx={{ alignSelf: { xs: 'stretch', sm: 'flex-start' } }}
            >
              {busy ? 'Turning on…' : 'Turn on'}
            </Button>
          )}
        </Stack>
      </CardContent>
    </Card>
  );
}
